import React, { useState, useEffect } from 'react';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload } from 'lucide-react';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shops, setShops] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);

  const [formData, setFormData] = useState({
    amount: '',
    category_id: '',
    shop_id: '',
    beneficiary_id: '',
    payment_method_id: '',
    notes: '',
  });

  // Sort function for alphabetical order
  const sortByName = (a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, categoriesRes, shopsRes, beneficiariesRes, paymentMethodsRes] =
        await Promise.all([
          api.get('/expenses'),
          api.get('/categories'),
          api.get('/shops'),
          api.get('/beneficiaries'),
          api.get('/payment-methods'),
        ]);

      setExpenses(expensesRes.data);
      // Sort all dropdowns alphabetically
      setCategories([...categoriesRes.data].sort(sortByName));
      setShops([...shopsRes.data].sort(sortByName));
      setBeneficiaries([...beneficiariesRes.data].sort(sortByName));
      setPaymentMethods([...paymentMethodsRes.data].sort(sortByName));
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        amount: expense.amount,
        category_id: expense.category_id,
        shop_id: expense.shop_id,
        beneficiary_id: expense.beneficiary_id,
        payment_method_id: expense.payment_method_id,
        notes: expense.notes || '',
      });
    } else {
      setEditingExpense(null);
      setFormData({
        amount: '',
        category_id: '',
        shop_id: '',
        beneficiary_id: '',
        payment_method_id: '',
        notes: '',
      });
      setReceiptFile(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExpense(null);
    setFormData({
      amount: '',
      category_id: '',
      shop_id: '',
      beneficiary_id: '',
      payment_method_id: '',
      notes: '',
    });
    setReceiptFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.id}`, {
          ...formData,
          amount: parseFloat(formData.amount),
        });
        toast.success('Spesa aggiornata con successo');
      } else {
        if (receiptFile) {
          const formDataWithFile = new FormData();
          formDataWithFile.append('amount', parseFloat(formData.amount));
          formDataWithFile.append('category_id', formData.category_id);
          formDataWithFile.append('shop_id', formData.shop_id);
          formDataWithFile.append('beneficiary_id', formData.beneficiary_id);
          formDataWithFile.append('payment_method_id', formData.payment_method_id);
          formDataWithFile.append('notes', formData.notes);
          formDataWithFile.append('receipt', receiptFile);

          await api.post('/expenses/with-receipt', formDataWithFile, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } else {
          await api.post('/expenses', {
            ...formData,
            amount: parseFloat(formData.amount),
          });
        }
        toast.success('Spesa aggiunta con successo');
      }

      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio della spesa');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/expenses/${expenseToDelete.id}`);
      toast.success('Spesa eliminata con successo');
      fetchData();
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
    } catch (error) {
      toast.error('Errore nell\'eliminazione della spesa');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-600">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Gestione Spese
          </h2>
          <p className="text-slate-600">Visualizza e gestisci tutte le tue spese</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full px-6 py-2 font-medium transition-all shadow-sm hover:shadow-md"
          data-testid="add-expense-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Aggiungi Spesa
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="expenses-table">
            <thead className="border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Data</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Importo</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Categoria</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Negozio</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Note</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {expenses.length > 0 ? (
                expenses.map((expense) => {
                  const category = categories.find((c) => c.id === expense.category_id);
                  const shop = shops.find((s) => s.id === expense.shop_id);
                  return (
                    <tr key={expense.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {formatDate(expense.date)}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${category?.color}20`,
                            color: category?.color,
                          }}
                        >
                          {category?.name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{shop?.name || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {expense.notes || '-'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(expense)}
                            className="text-slate-600 hover:text-[#D94E1F] hover:bg-[#D94E1F]/10"
                            data-testid={`edit-expense-${expense.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setExpenseToDelete(expense);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-slate-600 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-expense-${expense.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-slate-400">
                    Nessuna spesa registrata. Inizia aggiungendo la prima spesa!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col bg-white" data-testid="expense-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-slate-900">
              {editingExpense ? 'Modifica Spesa' : 'Aggiungi Nuova Spesa'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {editingExpense
                ? 'Modifica i dettagli della spesa esistente'
                : 'Inserisci i dettagli della nuova spesa'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto space-y-3 py-2 px-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="amount" className="text-slate-700 text-sm">Importo (€) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl text-base"
                    required
                    placeholder="0.00"
                    data-testid="expense-amount-input"
                  />
                </div>

                <div>
                  <Label htmlFor="category" className="text-slate-700 text-sm">Categoria *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                    required
                  >
                    <SelectTrigger className="mt-1.5 h-12 rounded-xl" data-testid="expense-category-select">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="shop" className="text-slate-700 text-sm">Negozio *</Label>
                  <Select
                    value={formData.shop_id}
                    onValueChange={(value) => setFormData({ ...formData, shop_id: value })}
                    required
                  >
                    <SelectTrigger className="mt-1.5 h-12 rounded-xl" data-testid="expense-shop-select">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {shops.map((shop) => (
                        <SelectItem key={shop.id} value={shop.id}>
                          {shop.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="beneficiary" className="text-slate-700 text-sm">Beneficiario *</Label>
                  <Select
                    value={formData.beneficiary_id}
                    onValueChange={(value) => setFormData({ ...formData, beneficiary_id: value })}
                    required
                  >
                    <SelectTrigger className="mt-1.5 h-12 rounded-xl" data-testid="expense-beneficiary-select">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {beneficiaries.map((ben) => (
                        <SelectItem key={ben.id} value={ben.id}>
                          {ben.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="payment_method" className="text-slate-700 text-sm">Modalità di Pagamento *</Label>
                <Select
                  value={formData.payment_method_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, payment_method_id: value })
                  }
                  required
                >
                  <SelectTrigger className="mt-1.5 h-12 rounded-xl" data-testid="expense-payment-method-select">
                    <SelectValue placeholder="Seleziona modalità" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {paymentMethods.map((pm) => (
                      <SelectItem key={pm.id} value={pm.id}>
                        {pm.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="notes" className="text-slate-700 text-sm">Note</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="mt-1.5 rounded-xl resize-none"
                  rows={2}
                  placeholder="Note aggiuntive..."
                  data-testid="expense-notes-input"
                />
              </div>

              {!editingExpense && (
                <div>
                  <Label htmlFor="receipt" className="text-slate-700 text-sm">Scontrino (opzionale)</Label>
                  <Input
                    id="receipt"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setReceiptFile(e.target.files[0])}
                    className="mt-1.5 h-12 rounded-xl file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-[#D94E1F]/10 file:text-[#D94E1F]"
                    data-testid="expense-receipt-input"
                  />
                </div>
              )}
            </div>
            <div className="flex-shrink-0 flex flex-col gap-2 pt-4 border-t border-slate-100 mt-2">
              <Button
                type="submit"
                className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl text-base font-medium"
                data-testid="save-expense-button"
              >
                {editingExpense ? 'Salva Modifiche' : 'Aggiungi'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                className="w-full h-12 rounded-xl text-base"
                data-testid="cancel-expense-button"
              >
                Annulla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white max-w-sm mx-4" data-testid="delete-expense-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900 text-xl font-bold">Sei sicuro?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 text-base">
              Questa azione non può essere annullata. La spesa verrà eliminata definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col">
            <AlertDialogAction
              onClick={handleDelete}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl text-base font-medium"
              data-testid="confirm-delete-button"
            >
              Elimina
            </AlertDialogAction>
            <AlertDialogCancel 
              className="w-full h-12 border-slate-300 text-slate-700 rounded-xl text-base font-medium mt-0"
              data-testid="cancel-delete-button"
            >
              Annulla
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Expenses;
